import React, { useState } from 'react';
import {
  Type,
  Hash,
  Calendar,
  CheckSquare,
  List,
  Paperclip,
  AlignLeft,
  Plus,
  ChevronDown,
  GripVertical,
  Trash2,
} from 'lucide-react';
import type { ExecutionFieldType, ExecutionField } from '../../../../types/card';

// ============================================================================
// UTILITY FUNCTIONS (exportadas para reuso em outros componentes)
// ============================================================================

/**
 * Retorna o label padrão (PT-BR) para cada tipo de campo de execucao.
 */
export function getDefaultLabel(type: ExecutionFieldType): string {
  const labels: Record<ExecutionFieldType, string> = {
    text: 'Campo de Texto',
    number: 'Valor Numerico',
    date: 'Data',
    checkbox: 'Verificacao',
    select: 'Selecao',
    currency: 'Valor (R$)',
    attachment: 'Anexo',
    file: 'Arquivo',
    textarea: 'Texto Longo',
  };
  return labels[type] || type;
}

/**
 * Retorna o icone Lucide correspondente ao tipo de campo.
 */
export function getFieldIcon(type: ExecutionFieldType): React.ReactNode {
  const icons: Record<ExecutionFieldType, React.ReactNode> = {
    text: <Type size={12} />,
    number: <Hash size={12} />,
    date: <Calendar size={12} />,
    checkbox: <CheckSquare size={12} />,
    select: <List size={12} />,
    currency: <span className="text-[10px] font-bold leading-none">R$</span>,
    attachment: <Paperclip size={12} />,
    file: <Paperclip size={12} />,
    textarea: <AlignLeft size={12} />,
  };
  return icons[type] ?? <Type size={12} />;
}

/**
 * Lista de tipos de campo disponiveis no Form Builder.
 */
export const FIELD_TYPES: Array<{ type: ExecutionFieldType; label: string }> = [
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'text', label: 'Texto' },
  { type: 'number', label: 'Numero' },
  { type: 'currency', label: 'Valor (R$)' },
  { type: 'date', label: 'Data' },
  { type: 'select', label: 'Selecao' },
  { type: 'textarea', label: 'Texto Longo' },
  { type: 'attachment', label: 'Anexo' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export interface FormFieldEditorProps {
  /** Lista atual de campos do formulario */
  fields: ExecutionField[];
  /** Callback disparado sempre que a lista de campos muda */
  onChange: (fields: ExecutionField[]) => void;
  /** Quando true, todos os controles ficam desabilitados / somente-leitura */
  disabled?: boolean;
}

/**
 * Gera um ID unico para novos campos de formulario.
 * Usa crypto.randomUUID quando disponivel, caso contrario gera um fallback.
 */
function generateFieldId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `field_${crypto.randomUUID()}`;
  }
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * FormFieldEditor - Editor de campos de formulario reutilizavel.
 *
 * Extraido do RightPanel para ser compartilhado entre RightPanel e
 * BlueprintStageEditor. Renderiza a lista de campos com controles
 * para adicionar, editar, reordenar (visual) e remover campos.
 */
export const FormFieldEditor: React.FC<FormFieldEditorProps> = ({
  fields,
  onChange,
  disabled = false,
}) => {
  const [showFieldTypeMenu, setShowFieldTypeMenu] = useState(false);

  // -- Handlers -----------------------------------------------------------------

  const handleAddField = (type: ExecutionFieldType) => {
    const newField: ExecutionField = {
      id: generateFieldId(),
      type,
      label: getDefaultLabel(type),
      required: false,
    };
    onChange([...fields, newField]);
    setShowFieldTypeMenu(false);
  };

  const handleUpdateField = (fieldId: string, updates: Partial<ExecutionField>) => {
    const updated = fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f));
    onChange(updated);
  };

  const handleRemoveField = (fieldId: string) => {
    onChange(fields.filter((f) => f.id !== fieldId));
  };

  // -- Render -------------------------------------------------------------------

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <List size={14} className="text-amber-600 dark:text-amber-300" />
          <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
            Campos do Formulario
          </label>
        </div>

        {/* Botao + Campo com dropdown */}
        {!disabled && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowFieldTypeMenu(!showFieldTypeMenu)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800 transition-colors"
            >
              <Plus size={12} />
              Campo
              <ChevronDown size={10} />
            </button>

            {/* Dropdown de tipos de campo */}
            {showFieldTypeMenu && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded shadow-lg z-50 py-1">
                {FIELD_TYPES.map(({ type, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleAddField(type)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-slate-500 dark:text-slate-400">
                      {getFieldIcon(type)}
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista de Campos */}
      {fields.length === 0 ? (
        <div className="text-center py-6 text-slate-400 dark:text-slate-500">
          <List size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-xs">Nenhum campo adicionado</p>
          <p className="text-[10px]">Clique em &quot;+ Campo&quot; para comecar</p>
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field) => (
            <div
              key={field.id}
              className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-600 group"
            >
              {/* Grip handle */}
              <GripVertical
                size={14}
                className={`text-slate-300 dark:text-slate-600 ${disabled ? 'cursor-default' : 'cursor-grab'}`}
              />

              {/* Icone do tipo de campo */}
              <span className="w-6 h-6 flex items-center justify-center bg-white dark:bg-slate-800 rounded text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600 shrink-0">
                {getFieldIcon(field.type)}
              </span>

              {/* Input do label */}
              <input
                type="text"
                value={field.label}
                onChange={(e) => handleUpdateField(field.id, { label: e.target.value })}
                disabled={disabled}
                className="flex-1 min-w-0 px-2 py-1 text-xs bg-transparent border-none focus:outline-none focus:ring-0 text-slate-700 dark:text-slate-300 disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Rotulo do campo"
              />

              {/* Toggle Obrigatorio / Opcional */}
              <button
                type="button"
                onClick={() => handleUpdateField(field.id, { required: !field.required })}
                disabled={disabled}
                className={`px-1.5 py-0.5 text-[9px] font-bold rounded transition-colors shrink-0 ${
                  field.required
                    ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {field.required ? 'REQ' : 'OPC'}
              </button>

              {/* Botao remover */}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveField(field.id)}
                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-all shrink-0"
                  title="Remover campo"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormFieldEditor;
